import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, PackagePlus, CheckCircle2, Rocket, ChevronDown, ChevronUp, Pencil, RotateCcw, Save, X } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";

function ExpandableTemplateCard({ template: t, companyId, onInstall, onEdit, busy }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-xl border border-border p-4 hover:border-primary/40 hover:shadow-sm transition-all flex flex-col"
      data-testid={`program-template-${t.template_id}`}
    >
      <div className="flex items-start gap-2">
        <Rocket className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm leading-snug break-words">{t.name}</p>
          {t.name_ar && (
            <p dir="rtl" className="text-xs text-muted-foreground mt-0.5 leading-snug break-words">
              {t.name_ar}
            </p>
          )}
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{t.category}</p>
        </div>
        {t.already_installed && (
          <Badge variant="outline" className="rounded-full text-[10px] gap-1">
            <CheckCircle2 className="h-3 w-3" /> Installed
          </Badge>
        )}
      </div>
      {t.focus && (
        <p
          className="text-[11px] italic text-muted-foreground mt-2 leading-snug break-words"
          data-testid={`program-template-focus-${t.template_id}`}
        >
          <span className="not-italic font-medium text-foreground/70">Focus:</span> {t.focus}
        </p>
      )}
      <p className="text-xs text-muted-foreground mt-2 flex-1 leading-snug break-words">{t.description}</p>

      {t.milestones && t.milestones.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-1 text-[11px] text-primary hover:underline"
            data-testid={`program-template-preview-${t.template_id}`}
          >
            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {open ? "Hide milestones" : `Preview ${t.milestones.length} milestones`}
          </button>
          {open && (
            <ol className="mt-2 space-y-1 text-[11px] text-muted-foreground list-decimal list-inside">
              {t.milestones.map((m, i) => (
                <li key={i} className="leading-snug break-words">
                  <span className="text-foreground">{m.title}</span>
                  {m.title_ar && (
                    <span dir="rtl" className="ms-1 text-muted-foreground/80">· {m.title_ar}</span>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
        <span className="text-[11px] text-muted-foreground">{t.milestone_count} milestones</span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="rounded-lg gap-1 h-8"
            onClick={() => onEdit(t)}
            data-testid={`program-template-edit-${t.template_id}`}
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </Button>
          <Button
            size="sm"
            className="rounded-lg gap-1"
            variant={t.already_installed && !companyId ? "outline" : "default"}
            onClick={() => onInstall(t)}
            disabled={busy}
            data-testid={`program-template-install-${t.template_id}`}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PackagePlus className="h-3.5 w-3.5" />}
            {companyId ? "Install & enroll" : t.already_installed ? "Install copy" : "Install"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * ProgramTemplatesCatalog — renders the shipped Programs library grouped
 * by category (Formation, Business Development, Product Launch, Investments).
 *
 * Two install modes controlled by the parent:
 *   - default (no `companyId`)  → clone the template into a fresh Program
 *     the admin can then enroll manually. Used inside AdminProgramsManager.
 *   - one-click enroll (`companyId` set) → clone AND enroll the given
 *     company atomically. Used inside the Company detail Program tab.
 *
 * Each card exposes an Edit action that opens a bilingual editor for the
 * template's name, focus, description and every milestone title — the
 * override is stored server-side so it applies across the platform (Journey
 * preview, business-type serializer, install action) without a code push.
 *
 * `onInstalled(programId)` fires after every successful install so the
 * parent can refresh its list / navigate.
 */
export function ProgramTemplatesCatalog({ companyId, onInstalled, dense = false }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [active, setActive] = useState("_all");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/program-templates");
      setItems(res.data?.items || []);
      setCategories(res.data?.categories || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const install = async (t) => {
    setBusy(t.template_id);
    try {
      const body = companyId ? { company_id: companyId } : {};
      const res = await client.post(`/admin/program-templates/${t.template_id}/install`, body);
      toast.success(
        companyId
          ? `Installed & enrolled — ${res.data?.enrolled?.milestones_cloned ?? 0} milestones`
          : `Installed — ${res.data?.milestone_count ?? 0} milestones cloned`
      );
      load();
      onInstalled && onInstalled(res.data.program_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Install failed");
    } finally {
      setBusy(null);
    }
  };

  const visible = active === "_all" ? items : items.filter((t) => t.category === active);

  return (
    <Card className={`rounded-2xl border border-border ${dense ? "p-4" : "p-6"} space-y-4`} data-testid="program-templates-catalog">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary flex-shrink-0">
          <PackagePlus className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h3 className="font-display font-semibold">Templates library</h3>
          <p className="text-xs text-muted-foreground">
            {companyId
              ? "One-click enroll — install a template and attach it to this company in one step."
              : "Starter programs curated for Saudi founders. Edit the bilingual copy or install to clone into your Programs list."}
          </p>
        </div>
      </div>

      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="rounded-xl flex-wrap h-auto">
          <TabsTrigger value="_all" className="rounded-lg" data-testid="program-templates-cat-all">All</TabsTrigger>
          {categories.map((c) => (
            <TabsTrigger key={c} value={c} className="rounded-lg" data-testid={`program-templates-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}>
              {c}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={active} className="mt-4">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : visible.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No templates in this category.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {visible.map((t) => (
                <ExpandableTemplateCard
                  key={t.template_id}
                  template={t}
                  companyId={companyId}
                  onInstall={install}
                  onEdit={setEditing}
                  busy={busy === t.template_id}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <TemplateEditDialog
        template={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </Card>
  );
}

// ----------------------------------------------------------------------------
// TemplateEditDialog — bilingual editor persisted via the overrides API.
// ----------------------------------------------------------------------------
function TemplateEditDialog({ template, onClose, onSaved }) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const L = (en, ar) => (isAr ? ar : en);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Snapshot the template into local editable form each time a template opens.
  useEffect(() => {
    if (!template) { setForm(null); return; }
    setForm({
      template_id: template.template_id,
      name: template.name || "",
      name_ar: template.name_ar || "",
      focus: template.focus || "",
      focus_ar: template.focus_ar || "",
      description: template.description || "",
      description_ar: template.description_ar || "",
      milestones: (template.milestones || []).map((m) => ({
        title: m.title || "",
        title_ar: m.title_ar || "",
        description: m.description || "",
        stage: m.stage || "",
      })),
    });
  }, [template]);

  if (!template || !form) return null;

  const patchField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const patchMs = (idx, key, value) => setForm((prev) => ({
    ...prev,
    milestones: prev.milestones.map((m, i) => (i === idx ? { ...m, [key]: value } : m)),
  }));

  const save = async () => {
    setSaving(true);
    try {
      await client.patch(`/admin/program-templates/${template.template_id}`, {
        name: form.name,
        name_ar: form.name_ar,
        focus: form.focus,
        focus_ar: form.focus_ar,
        description: form.description,
        description_ar: form.description_ar,
        milestones: form.milestones,
      });
      toast.success("Template updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setResetting(true);
    try {
      await client.delete(`/admin/program-templates/${template.template_id}/override`);
      toast.success("Reset to defaults");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <Dialog open={!!template} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="template-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{L("Edit template", "تعديل القالب")} — <span dir="ltr" className="inline-block">{template.name}</span></DialogTitle>
          <DialogDescription dir={isAr ? "rtl" : "ltr"}>
            {L(
              "Overrides apply everywhere this template is shown (Journey preview, install action, business-type selector).",
              "تنطبق التعديلات على كل مكان يظهر فيه هذا القالب (معاينة الرحلة، إجراء التثبيت، محدد نوع النشاط).",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">{L("Name (EN)", "الاسم (إنجليزي)")}</Label>
              <Input dir="ltr" value={form.name} onChange={(e) => patchField("name", e.target.value)} className="rounded-xl mt-1" data-testid="template-edit-name-en" />
            </div>
            <div>
              <Label className="text-xs">{L("Name (AR)", "الاسم (عربي)")}</Label>
              <Input dir="rtl" value={form.name_ar} onChange={(e) => patchField("name_ar", e.target.value)} className="rounded-xl mt-1" data-testid="template-edit-name-ar" />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">{L("Focus (EN)", "التركيز (إنجليزي)")}</Label>
              <Input dir="ltr" value={form.focus} onChange={(e) => patchField("focus", e.target.value)} className="rounded-xl mt-1" data-testid="template-edit-focus-en" />
            </div>
            <div>
              <Label className="text-xs">{L("Focus (AR)", "التركيز (عربي)")}</Label>
              <Input dir="rtl" value={form.focus_ar} onChange={(e) => patchField("focus_ar", e.target.value)} className="rounded-xl mt-1" data-testid="template-edit-focus-ar" />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">{L("Description (EN)", "الوصف (إنجليزي)")}</Label>
              <textarea dir="ltr" value={form.description} onChange={(e) => patchField("description", e.target.value)} rows={3} className="mt-1 w-full rounded-xl border border-input bg-transparent p-3 text-sm" data-testid="template-edit-desc-en" />
            </div>
            <div>
              <Label className="text-xs">{L("Description (AR)", "الوصف (عربي)")}</Label>
              <textarea dir="rtl" value={form.description_ar} onChange={(e) => patchField("description_ar", e.target.value)} rows={3} className="mt-1 w-full rounded-xl border border-input bg-transparent p-3 text-sm" data-testid="template-edit-desc-ar" />
            </div>
          </div>

          <div>
            <p className="font-display text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              {L("Milestones (bilingual)", "الخطوات (ثنائية اللغة)")}
            </p>
            <ol className="space-y-3">
              {form.milestones.map((m, i) => (
                <li key={i} className="rounded-xl border border-border p-3 space-y-2" data-testid={`template-edit-ms-${i}`}>
                  <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    {L(`Step ${i + 1}`, `الخطوة ${i + 1}`)}
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input
                      dir="ltr"
                      value={m.title}
                      onChange={(e) => patchMs(i, "title", e.target.value)}
                      placeholder={L("Title (EN)", "العنوان (إنجليزي)")}
                      className="rounded-lg"
                      data-testid={`template-edit-ms-title-en-${i}`}
                    />
                    <Input
                      dir="rtl"
                      value={m.title_ar}
                      onChange={(e) => patchMs(i, "title_ar", e.target.value)}
                      placeholder={L("Title (AR)", "العنوان (عربي)")}
                      className="rounded-lg"
                      data-testid={`template-edit-ms-title-ar-${i}`}
                    />
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row sm:justify-between gap-2 pt-2">
          <Button variant="outline" className="rounded-xl gap-2" onClick={reset} disabled={resetting || saving} data-testid="template-edit-reset">
            {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            {L("Reset to defaults", "إعادة الضبط")}
          </Button>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" className="rounded-xl gap-2" onClick={onClose} data-testid="template-edit-cancel">
              <X className="h-4 w-4" /> {L("Cancel", "إلغاء")}
            </Button>
            <Button className="btn-brand rounded-xl gap-2" onClick={save} disabled={saving} data-testid="template-edit-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {L("Save overrides", "حفظ التعديلات")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ProgramTemplatesCatalog;
